/**
 * Starter templates for the guided skill builder. Each gives the user a well-structured `SKILL.md` for a
 * common use case, so a non-technical author can produce a skill the on-device agent uses reliably. The
 * picker in {@link SkillEditor} (create mode only) drops the chosen template's name/description/keywords/
 * body into the form for editing. Template copy is intentionally plain English (it's authored content,
 * not app chrome); the picker's labels are localized.
 */
import { SkillActivation } from '@shared/models/skills/SkillModel';

export interface SkillTemplate {
    /** Stable id used as the picker key. */
    id: string;
    /** i18n key for the short picker label. */
    titleKey: string;
    /** English fallback label for the picker. */
    titleFallback: string;
    /** i18n key for the one-line picker summary. */
    summaryKey: string;
    /** English fallback summary for the picker. */
    summaryFallback: string;
    /** Prefilled skill name (spec-valid). Empty for the blank template. */
    suggestedName: string;
    /** Prefilled description. */
    description: string;
    /** Prefilled keywords. */
    keywords: string[];
    /** Activation to force when this template is applied. Omit → leave the editor's default in place. */
    activation?: SkillActivation;
    /** Prefilled SKILL.md body. Empty for the blank template (the service scaffolds a default). */
    body: string;
}

export const SKILL_TEMPLATES: SkillTemplate[] = [
    {
        id: 'blank',
        titleKey: 'common:skills.templates.blank.title',
        titleFallback: 'Blank',
        summaryKey: 'common:skills.templates.blank.summary',
        summaryFallback: 'Start from an empty template.',
        suggestedName: '',
        description: '',
        keywords: [],
        body: '',
    },
    {
        id: 'navigate-app',
        titleKey: 'common:skills.templates.navigateApp.title',
        titleFallback: 'Navigate an app (click in steps)',
        summaryKey: 'common:skills.templates.navigateApp.summary',
        summaryFallback: 'Tell Cephable where to click, step by step, to get through an app.',
        suggestedName: 'navigate-my-app',
        description:
            'Walk through <App Name> by clicking the right buttons in order. Use when I ask to open, ' +
            'navigate, or do a multi-step task in <App Name>.',
        keywords: ['navigate', 'click', 'open', 'app'],
        activation: 'manual',
        body: `# Navigate <App Name>

Step-by-step clicks to get through <App Name>. Replace <App Name> and the button names below with the
real ones from your app.

## When to use this skill
When I ask to open <App Name> or to do a task in it that takes several clicks.

## Steps
1. Make sure <App Name> is the active window. If it isn't, ask me to open it first.
2. Click the "<First button>" button (top-left toolbar).
3. Wait for the "<Panel/dialog name>" to appear, then click "<Next button>".
4. If a confirmation dialog appears, click "Confirm".
5. Tell me when the task is done, or what you see if a step's button isn't there.

## Tips & best practices
- Refer to buttons by their visible label ("Save", "New", "Settings"), not their position, when you can.
- If a button can't be found, describe what's on screen and ask me how to proceed instead of guessing.
- Do one step at a time and check the result before the next click.

## Example
"Open a new invoice in <App Name>" → click New → click Invoice → confirm the blank invoice opened.
`,
    },
    {
        id: 'write-text',
        titleKey: 'common:skills.templates.writeText.title',
        titleFallback: 'Write or insert text',
        summaryKey: 'common:skills.templates.writeText.summary',
        summaryFallback: 'Draft text a certain way and type it where I am working.',
        suggestedName: 'write-in-my-style',
        description:
            'Write text in my preferred style and type it into the active app. Use when I ask to draft, ' +
            'write, or insert a message, note, or reply.',
        keywords: ['write', 'draft', 'type', 'insert', 'message'],
        body: `# Write In My Style

Draft text the way I like it, then type it where my cursor is.

## When to use this skill
When I ask you to write, draft, reply to, or insert text somewhere.

## Style rules
- Tone: <friendly / professional / concise — pick one>.
- Length: keep it short unless I ask for more.
- Never include placeholders like "[name]" — ask me if you're missing a detail.

## Steps
1. Understand what I want written and who it's for.
2. Draft the text following the style rules above.
3. Show me the draft first if it's important or long; otherwise type it into the active field.
4. Type the text where my cursor is (do not clear existing text unless I ask).

## Example
"Reply thanks and that I'll review it tomorrow" → a short, friendly reply typed into the message box.
`,
    },
    {
        id: 'fill-form',
        titleKey: 'common:skills.templates.fillForm.title',
        titleFallback: 'Fill out a form',
        summaryKey: 'common:skills.templates.fillForm.summary',
        summaryFallback: 'Fill a form field by field from details I give you.',
        suggestedName: 'fill-my-form',
        description:
            'Fill out the <Form Name> form field by field from the details I provide. Use when I ask to ' +
            'complete or fill in <Form Name>.',
        keywords: ['form', 'fill', 'fields', 'enter', 'submit'],
        activation: 'manual',
        body: `# Fill Out <Form Name>

Complete <Form Name> one field at a time. Replace the field names below with the real ones.

## When to use this skill
When I ask you to fill in or complete <Form Name>.

## Fields (in order)
1. "<Field 1 label>" — <what goes here>.
2. "<Field 2 label>" — <what goes here>.
3. "<Field 3 label>" — <what goes here>.

## Steps
1. Click the first field, then type its value.
2. Move to the next field (click it, or press Tab) and type its value.
3. Repeat for every field. If I didn't give a value for a field, ask me — don't invent one.
4. Do NOT click Submit/Save unless I tell you to. Show me the filled form first.

## Tips & best practices
- Double-check each value after typing it.
- If a field is a dropdown, tell me the options and let me choose.

## Example
"Fill the contact form with my name and email" → Name and Email filled, waiting for me to submit.
`,
    },
    {
        id: 'summarize-doc',
        titleKey: 'common:skills.templates.summarizeDoc.title',
        titleFallback: 'Summarize a document',
        summaryKey: 'common:skills.templates.summarizeDoc.summary',
        summaryFallback: 'Read a file and give a short, structured summary.',
        suggestedName: 'summarize-document',
        description:
            'Read a document and produce a short, structured summary. Use when I ask to summarize, ' +
            'recap, or pull the key points from a file.',
        keywords: ['summarize', 'summary', 'document', 'recap', 'key points'],
        body: `# Summarize A Document

Read a file I point you at and summarize it clearly.

## When to use this skill
When I ask you to summarize, recap, or list the key points of a document or file.

## Steps
1. Read the file with your file tools (I'll attach it or tell you where it is).
2. Identify the main topic and the 3–5 most important points.
3. Write the summary in this shape:
   - **Topic:** one line.
   - **Key points:** a short bulleted list.
   - **Action items:** anything I need to do (omit if none).
4. Keep it brief — aim for well under one screen.

## Tips & best practices
- Use plain language; avoid copying long passages verbatim.
- If the document is very long, summarize section by section, then give an overall summary.

## Example
"Summarize this contract" → Topic, key terms as bullets, and any deadlines as action items.
`,
    },
];
